<template>
  <div class="location-coverage-bar">
    <h2 class="global-h4 location-coverage-bar__title">Promotion coverage</h2>
    <p class="location-coverage-bar__subtitle">{{ highlighted }} of {{ total }} locations highlighted</p>

    <div class="location-coverage-bar__track">
      <div v-if="total > 0" class="location-coverage-bar__fill location-coverage-bar__fill--highlighted" :style="{ width: `${highlightedPercent}%` }"></div>
      <div v-if="total > 0" class="location-coverage-bar__fill location-coverage-bar__fill--not-yet" :style="{ width: `${100 - highlightedPercent}%` }"></div>
    </div>

    <div v-if="categoryCoverage.length" class="location-coverage-bar__categories">
      <!--<h3 class="location-coverage-bar__categories-title">By type</h3>-->
      <ul :id="categoryListId" class="location-coverage-bar__category-list">
        <li v-for="category in visibleCategoryCoverage" :key="category.option.value" class="location-coverage-bar__category-row">
          <div class="location-coverage-bar__category-label">
            <LocationCategoryIcon :option="category.option" :size="24" aria-hidden="true" />
            <span>{{ category.option.label }}</span>
          </div>
          <div
            class="location-coverage-bar__category-track"
            role="progressbar"
            :aria-label="`${category.option.label} locations highlighted`"
            :aria-valuemin="0"
            :aria-valuemax="category.totalCount"
            :aria-valuenow="category.highlightedCount"
            :aria-valuetext="`${category.highlightedCount} of ${category.totalCount} locations highlighted`"
          >
            <div class="location-coverage-bar__category-fill" :style="{ width: `${(100 * category.highlightedCount) / category.totalCount}%` }"></div>
          </div>
          <span class="location-coverage-bar__category-count"
            ><strong>{{ category.highlightedCount }}</strong> / {{ category.totalCount }}</span
          >
        </li>
      </ul>
      <button
        v-if="hiddenCategoryCount > 0"
        type="button"
        class="location-coverage-bar__category-toggle"
        :aria-expanded="isShowingAllTypes"
        :aria-controls="categoryListId"
        :aria-label="isShowingAllTypes ? 'Show fewer types' : `Show ${hiddenCategoryCount} more types`"
        @click="isShowingAllTypes = !isShowingAllTypes"
      >
        <IconEmbedded :name="isShowingAllTypes ? 'caret-top_2-5' : 'caret-bottom_2-5'" :size="12" />
      </button>
    </div>

    <!--<div class="location-coverage-bar__legend">
      <span class="location-coverage-bar__legend-item">
        <span class="location-coverage-bar__dot location-coverage-bar__dot--highlighted"></span>
        Highlighted {{ highlighted }}
      </span>
      <span class="location-coverage-bar__legend-item">
        <span class="location-coverage-bar__dot location-coverage-bar__dot--not-yet"></span>
        Not yet highlighted {{ notYetCount }}
      </span>
    </div>

    <div class="location-coverage-bar__totals">
      <div class="location-coverage-bar__total">
        <span class="location-coverage-bar__total-value">{{ props.postCount }}</span>
        {{ props.postCount === 1 ? "post" : "posts" }}
      </div>
      <div class="location-coverage-bar__total">
        <span class="location-coverage-bar__total-value">{{ highlighted }}</span>
        {{ highlighted === 1 ? "location promoted" : "locations promoted" }}
      </div>
    </div>
    -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from "vue";
import type { LocationHighlightRow } from "@contracts/socialReports";
import type { LocationData } from "@contracts/socialLocations";
import { buildLocationCoverageByCategory } from "@logic/LocationCoverageByCategory";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = defineProps<{
  highlightedCount: number;
  totalCount: number;
  /** Distinct posts that feature at least one location. */
  postCount: number;
  rows: LocationHighlightRow[];
  locationsById: Record<string, LocationData>;
}>();

const total = computed<number>(() => Math.max(0, props.totalCount));
const highlighted = computed<number>(() => Math.max(0, Math.min(props.highlightedCount, total.value)));
const notYetCount = computed<number>(() => total.value - highlighted.value);
const highlightedPercent = computed<number>(() => (total.value === 0 ? 0 : (100 * highlighted.value) / total.value));
const categoryCoverage = computed(() => buildLocationCoverageByCategory(props.rows, props.locationsById));
const DEFAULT_VISIBLE_TYPE_COUNT = 4;
const categoryListId = useId();
const isShowingAllTypes = ref(false);
const visibleCategoryCoverage = computed(() => (isShowingAllTypes.value ? categoryCoverage.value : categoryCoverage.value.slice(0, DEFAULT_VISIBLE_TYPE_COUNT)));
const hiddenCategoryCount = computed(() => Math.max(0, categoryCoverage.value.length - DEFAULT_VISIBLE_TYPE_COUNT));
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Location coverage bar ======================================================
.location-coverage-bar {
  height: 100%;
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__title {
    margin: 0 0 4px;
  }

  &__subtitle {
    margin: 0 0 18px;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__track {
    width: 100%;
    height: 14px;
    display: flex;
    border-radius: 100px;
    overflow: hidden;
    background: #e4e7ec;
  }

  &__fill {
    height: 100%;
    transition: width 0.2s ease-in-out;

    &--highlighted {
      background: #058587;
    }

    &--not-yet {
      background: #f2b84b;
    }
  }

  &__categories {
    margin-top: 16px;
  }

  &__categories-title {
    margin: 0 0 10px;
    color: #667085;
    font: 400 13px/18px sans-serif;
  }

  &__category-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__category-row {
    display: grid;
    grid-template-columns: minmax(110px, 1.2fr) minmax(40px, 1fr) max-content;
    align-items: center;
    gap: 10px;
    color: #475467;
    font: 400 13px/17px sans-serif;
  }

  &__category-label {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }

  &__category-track {
    height: 8px;
    border-radius: 100px;
    overflow: hidden;
    background: #eef1f4;
  }

  &__category-fill {
    height: 100%;
    border-radius: inherit;
    background: #058587;
    transition: width 0.2s ease-in-out;
  }

  &__category-count {
    min-width: 37px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  &__category-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-top: 8px;
    padding: 0;
    border: 0;
    background: none;
    color: #667085;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #058587;
      outline-offset: 4px;
    }
  }

  &__legend {
    margin-top: 14px;
    gap: 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
  }

  &__totals {
    margin-top: 18px;
    padding-top: 16px;
    gap: 4px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid #e4e7ec;
  }

  &__total {
    gap: 12px;
    display: flex;
    align-items: baseline;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__total-value {
    min-width: 18px;
    color: #102f41;
    font: 500 26px/32px $font-family;
  }

  &__dot {
    width: 9px;
    height: 9px;
    margin-right: 7px;
    border-radius: 100px;

    &--highlighted {
      background: #058587;
    }

    &--not-yet {
      background: #f2b84b;
    }
  }
}
</style>
